/**
 * Recent Product Analyzer lookups (client-side).
 * Demo never auto-loads; history is for re-open / re-analyze only.
 */

const STORAGE_KEY = "ezisell.analyzer.recent.v1";
const MAX_ITEMS = 12;

/** Starter rows so an empty workspace still has working re-analyze targets. */
export const ANALYZER_HISTORY_SEED = [
  {
    id: "408362440",
    itemId: "408362440",
    title: "Tide PODS Laundry Detergent Soap Pacs, Ultra OXI, 32 Count",
    price: 12.97,
    image:
      "https://i5.walmartimages.com/asr/0d6d9177-17b6-4ec4-8ec0-781947bdb44f.jpeg",
    analyzedAt: null,
    seed: true,
    signals: ["In stock", "Walmart.com", "Live PDP"],
  },
  {
    id: "9650019994",
    itemId: "9650019994",
    title: "Tide Powder Laundry Detergent, Original Scent, 50 oz, 44 Loads",
    price: 12.57,
    image:
      "https://i5.walmartimages.com/seo/Tide-Laundry-Detergent-Powder-Ultra-Compact-Low-Suds-Original-Regular_833e2099-f0dd-4125-9e37-0e6e2132e11e.2373fa772847edd7841857b7e2f84963.jpeg",
    analyzedAt: null,
    seed: true,
    signals: ["In stock", "Walmart.com", "Live PDP"],
  },
  {
    id: "627242735",
    itemId: "627242735",
    title: "KILZ 3 Premium Interior/Exterior Primer, 1 Quart",
    price: 14.97,
    image:
      "https://i5.walmartimages.com/asr/7fda2df2-aa42-4e10-8d99-00b88ee04056.jpeg",
    analyzedAt: null,
    seed: true,
    signals: ["In stock", "Walmart.com", "Live PDP"],
  },
];

function safeParse(raw) {
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

/** @returns {typeof ANALYZER_HISTORY_SEED} */
export function readAnalyzerHistory() {
  if (typeof window === "undefined" || !window.localStorage) {
    return [...ANALYZER_HISTORY_SEED];
  }
  const stored = safeParse(window.localStorage.getItem(STORAGE_KEY) || "[]");
  if (!stored.length) return [...ANALYZER_HISTORY_SEED];
  return stored.slice(0, MAX_ITEMS);
}

/**
 * @param {object} entry
 */
export function rememberAnalyzerLookup(entry) {
  if (!entry || typeof window === "undefined" || !window.localStorage) return;
  const itemId = String(entry.itemId || entry.id || "").replace(/\D/g, "");
  if (!itemId && !entry.query) return;

  const next = {
    id: itemId || entry.query,
    itemId: itemId || null,
    query: entry.query || itemId || null,
    title: entry.title || `Walmart item ${itemId}`,
    price: entry.price ?? null,
    image: entry.image || null,
    analyzedAt: entry.analyzedAt || new Date().toISOString(),
    seed: false,
    signals: entry.signals || ["Live PDP"],
  };

  const prev = safeParse(window.localStorage.getItem(STORAGE_KEY) || "[]")
    .filter((r) => String(r.id) !== String(next.id));
  const merged = [next, ...prev].slice(0, MAX_ITEMS);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
}

/**
 * Build a history entry from a loaded analyzer product payload.
 * @param {object} product
 */
export function historyEntryFromProduct(product) {
  if (!product || typeof product !== "object") return null;
  const w = product.walmart || {};
  const itemId = String(w.item_id_clean || w.walmart_item_id || product.id || "")
    .replace(/\D/g, "");
  if (!itemId) return null;
  const signals = ["Live PDP"];
  if (w.availability === true || /in\s*stock/i.test(String(w.availability_label || ""))) {
    signals.unshift("In stock");
  }
  const fulfillment = w.fulfillment_type || w.sold_by || null;
  if (fulfillment) signals.push(String(fulfillment));
  else signals.push("Walmart.com");
  return {
    id: itemId,
    itemId,
    title: w.title || product.match?.title || `Walmart item ${itemId}`,
    price: typeof w.price === "number" ? w.price : Number(w.price) || null,
    image: w.image || (Array.isArray(w.images) ? w.images[0] : null) || null,
    analyzedAt: new Date().toISOString(),
    signals,
  };
}

/** @param {string|null|undefined} iso */
export function formatAnalyzerHistoryWhen(iso) {
  if (!iso) return "Suggested";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Recently";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  if (sameDay) return `Today, ${time}`;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
